import { cx } from '@emotion/css';
import type { CSSProperties, ReactNode } from 'react';
import { forwardRef, useContext, useRef } from 'react';

import { BrowserFeaturesContext } from '../../BrowserFeatures/BrowserFeaturesContextProvider';
import { Device } from '../../constants';
import { MotifComponent, useMotifStyles } from '../../motif';
import { combineRefs } from '../../utils/useCombinesRefs';
import { PictureWithStickers } from '../Picture';
import { VideoWithStickers } from '../Video';
import { LaptopWrapper } from './LaptopWrapper';
import {
  divStyleCss,
  macSafariCss,
  noWrapBackgroundVideoCss,
  noWrapImageCss,
  noWrapVideoCss,
  phoneContainerCss,
  phoneVideoCss,
  shadowContainerCss,
  wrappedBackgroundVideoCss,
  wrappedImageCss,
  wrappedVideoCss,
} from './Media.styled';
import { PhoneWrapper } from './PhoneWrapper';
import type { MediaWithStickersProps } from './types';

const mediaWrapperClassName = 'sdsm-media-wrapper';

export const MediaWithStickers = forwardRef<HTMLElement, MediaWithStickersProps>((props, ref) => {
  const {
    className,
    mediaClassName,
    altText,
    imageSource,
    maxHeight,
    maxWidth,
    videoSource,
    mobileVideoSource,
    sourceType,
    wrap = Device.None,
    // Don't expect this to work unless you're passing a video
    showVideoControls = false,
    autoplay = !showVideoControls,
    posterSource,
    onPlay,
    captionsSource,
    isBackgroundVideo,
    onTimeUpdate,
    imgSrcs,
    dataset,
    height,
    width,
    isDraggable,
    stickers,
  } = props;

  useMotifStyles(MotifComponent.MEDIA);

  let unwrapped: ReactNode = null;
  const { getLowEntropyHints } = useContext(BrowserFeaturesContext);
  const mediaRef = useRef<HTMLElement>(null);
  const clientHints = getLowEntropyHints();
  const isMacSafari = !!(
    clientHints.platform === 'macOS' &&
    clientHints.browsers.find(browser => browser.brand === 'Safari')
  );
  const applyMacSafariStyles = isMacSafari && wrap === Device.Phone;

  const isDevice = wrap === Device.Laptop || wrap === Device.Phone;

  const sizeStyles: CSSProperties = {
    maxHeight: maxHeight && !isDevice ? `${maxHeight}px` : undefined,
    maxWidth: maxWidth && !isDevice ? `${maxWidth}px` : undefined,
  };

  if (imageSource || imgSrcs) {
    const imgClassName = cx(
      {
        [wrappedImageCss]: isDevice,
        [noWrapImageCss]: !isDevice,

        [phoneContainerCss]: wrap === Device.Phone,
        [shadowContainerCss]: wrap === Device.Shadow,
      },
      // Comes last to do overrides.
      mediaClassName
    );

    if (imgSrcs) {
      unwrapped = (
        <PictureWithStickers
          className={cx(MotifComponent.MEDIA, className)}
          imgSrcs={imgSrcs}
          altText={altText}
          imgClassName={imgClassName}
          style={sizeStyles}
          dataset={dataset}
          height={height}
          width={width}
          isDraggable={isDraggable}
          stickers={isDevice ? undefined : stickers}
        />
      );
    } else if (imageSource) {
      // if the image will not be wrapped inside a media wrapper, use the height
      // here. Otherwise, the wrapper will implement the height
      unwrapped = (
        <PictureWithStickers
          className={cx(MotifComponent.MEDIA, className)}
          altText={altText}
          imgClassName={imgClassName}
          style={sizeStyles}
          defaultSrc={imageSource}
          dataset={dataset}
          height={height}
          width={width}
          isDraggable={isDraggable}
          stickers={isDevice ? undefined : stickers}
        />
      );
    }
  } else if (videoSource) {
    const wrappedVideo2Css = isBackgroundVideo ? wrappedBackgroundVideoCss : wrappedVideoCss;
    const noWrapVideo2Css = isBackgroundVideo ? noWrapBackgroundVideoCss : noWrapVideoCss;
    const videoClassName = cx(
      {
        [phoneVideoCss]: wrap === Device.Phone,
        [wrappedVideo2Css]: isDevice,
        [macSafariCss]: wrap === Device.Phone && applyMacSafariStyles,
        [noWrapVideo2Css]: !isDevice,
        [shadowContainerCss]: wrap === Device.Shadow,
      },
      mediaClassName
    );

    unwrapped = (
      <VideoWithStickers
        key={videoSource}
        source={videoSource}
        mobileSource={mobileVideoSource}
        sourceType={sourceType}
        altText={altText}
        className={cx(MotifComponent.MEDIA, className)}
        videoClassName={videoClassName}
        style={sizeStyles}
        autoPlay={autoplay}
        playsInline={!showVideoControls}
        loop={!showVideoControls}
        muted={!showVideoControls}
        controls={showVideoControls}
        posterSource={posterSource}
        onPlay={onPlay}
        captionsSource={captionsSource}
        onTimeUpdate={onTimeUpdate}
        isBackgroundVideo={isBackgroundVideo}
        dataset={dataset}
        ref={combineRefs(mediaRef, ref)}
        stickers={isDevice ? undefined : stickers}
      />
    );

    if (applyMacSafariStyles) {
      unwrapped = (
        <div data-testid="sdsm-media-safari-wrapper" className={divStyleCss}>
          {unwrapped}
        </div>
      );
    }
  } else {
    // early escape in case there is no media
    return null;
  }

  if (wrap === Device.Laptop) {
    return (
      <LaptopWrapper className={mediaWrapperClassName} maxHeight={maxHeight}>
        {unwrapped}
      </LaptopWrapper>
    );
  }

  if (wrap === Device.Phone) {
    return (
      <PhoneWrapper className={mediaWrapperClassName} maxHeight={maxHeight}>
        {unwrapped}
      </PhoneWrapper>
    );
  }

  return (
    // TODO: Delete this nonsense div. It serves no purpose.
    <div className={mediaWrapperClassName} data-testid="no-device-wrapper">
      {unwrapped}
    </div>
  );
});

MediaWithStickers.displayName = 'MediaWithStickers';
